import { api, CommonListParams, Response } from "~/src/lib/api";
import { Address } from "~/src/lib/api/address";
import { Department } from "~/src/lib/api/departments";
import { Disability } from "~/src/lib/api/disabilities";
import { Education } from "~/src/lib/api/education";
import { Ethnicity } from "~/src/lib/api/ethnicities";
import { ExtracurricularType } from "~/src/lib/api/extracurricular-types";
import { Gender } from "~/src/lib/api/genders";
import { Id } from "~/src/lib/api/id";
import { JobLevel } from "~/src/lib/api/job-levels";
import { MaritalStatus } from "~/src/lib/api/marital-statuses";
import { SexualOrientation } from "~/src/lib/api/sexual-orientations";
import { SkillLevel } from "~/src/lib/api/skill-levels";
import { Skill } from "~/src/lib/api/skills";
import { Upload } from "~/src/lib/api/uploads";
import { compareStartDateInverted } from "~/src/lib/comparers";

/**
 * Applicant registration status.
 */
export enum Status {
  Active = "active",
  PendingPersonalData = "pendingPersonalData",
  PendingContactData = "pendingContactData",
  PendingEducationData = "pendingEducationData",
  PendingExperienceData = "pendingExperienceData",
}

/**
 * Experience model.
 */
export type Experience = Partial<{
  id: Id;
  organization: string;
  role: string;
  departmentId: Id;
  department: Department;
  jobLevelId: Id;
  jobLevel: JobLevel;
  type: "professional" | "exchange program" | "course";
  description: string;
  startDate: string;
  endDate: string;
}>;

/**
 * Extracurricular model.
 */
export type Extracurricular = Partial<{
  id: Id;
  extracurricularTypeId: Id;
  extracurricularType: ExtracurricularType;
  name: string;
  organization: string;
  startDate: string;
  endDate: string;
  description: string;
}>;

/**
 * Skill relation model.
 */
export type SkillApplicantRelation = Partial<{
  skillId: Id;
  skill: Skill;
  skillLevelId: Id;
  skillLevel: SkillLevel;
}>;

export enum Pool {
  Company = "company",
  Bluy = "bluy",
}

/**
 * Applicant model.
 */
export type Applicant = Partial<{
  id: Id;
  createdAt: string;
  updatedAt: string;
  name: string;
  status: Status;
  taxNumber: string;
  email: string;
  mobile: string;
  pictureId: Id;
  picture: Upload;
  birthday: string;
  age: number;
  ethnicityId: Id;
  ethnicity: Ethnicity;
  genderId: Id;
  gender: Gender;
  maritalStatusId: Id;
  maritalStatus: MaritalStatus;
  linkedin: string;
  facebook: string;
  instagram: string;
  tiktok: string;
  twitter: string;
  disabilityIds: Id[];
  disabilities: Disability[];
  location: Address;
  educationIds: Id[];
  education: Education[];
  experienceIds: Id[];
  experience: Experience[];
  extracurricularIds: Id[];
  extracurriculars: Extracurricular[];
  skills: SkillApplicantRelation[];
  languages: SkillApplicantRelation[];
  sexualOrientationId: Id;
  sexualOrientation: SexualOrientation;
  recurring: boolean;
  hasActiveWorkContract: boolean;
}>;

/**
 * Get parameters.
 */
type GetParams = {
  id: Id;
};

/**
 * List parameters.
 */
export type ListParams = CommonListParams & { pool?: string };

/**
 * Get the query key for the resource.
 * @see https://react-query.tanstack.com/guides/query-keys
 * @param params Query parameters.
 * @returns Query key.
 */
export const getQueryKey = (params?: GetParams | ListParams) => {
  return ["applicants", params].filter(Boolean);
};

/**
 * Get a single applicant by id.
 * @param id Id of the applicant.
 * @returns A promise of the response.
 */
export const get = async ({ id }: GetParams) => {
  return (await api.get<Response<Applicant>>(`/applicants/${id}`)).data;
};

/**
 * Create a new applicant.
 * @param data New applicant data.
 * @returns A promise of the response.
 */
export const create = async (data: {
  email?: string;
  token?: string;
  jobId: Id;
}) => {
  return (
    await api.post<Response<Applicant>>("/applicants", data, { public: true })
  ).data;
};

/**
 * List applicants.
 * @param opts Query options.
 * @returns A promise of the response.
 */
export const list = async (params: ListParams) => {
  return (
    await api.get<Response<Applicant[]>>("/applicants", {
      params,
    })
  ).data;
};

/**
 * Update applicant.
 * @param applicant Existing applicant data with id.
 * @returns A promise of the response.
 */
export const update = async ({ id, ...data }: Applicant) => {
  return (await api.patch<Response<Applicant>>(`/applicants/${id}`, data)).data;
};

/**
 * Tells if a given applicant is active.
 * @param applicant Applicant object.
 * @returns True if applicant is active, false otherwise.
 */
export const isActive = (applicant: Applicant | undefined) => {
  return applicant?.status === Status.Active;
};

/**
 * Tells if a given applicant is pending personal data.
 * @param applicant Applicant object.
 * @returns True if applicant is pending personal data, false otherwise.
 */
export const isPendingPersonalData = (applicant: Applicant | undefined) => {
  return applicant?.status === Status.PendingPersonalData;
};

/**
 * Tells if a given applicant is pending contact data.
 * @param applicant Applicant object.
 * @returns True if applicant is pending contact data, false otherwise.
 */
export const isPendingContactData = (applicant: Applicant | undefined) => {
  return applicant?.status === Status.PendingContactData;
};

/**
 * Tells if a given applicant is pending education data.
 * @param applicant Applicant object.
 * @returns True if applicant is pending education data, false otherwise.
 */
export const isPendingEducationData = (applicant: Applicant | undefined) => {
  return applicant?.status === Status.PendingEducationData;
};

/**
 * Tells if a given applicant is pending experience data.
 * @param applicant Applicant object.
 * @returns True if applicant is pending experience data, false otherwise.
 */
export const isPendingExperienceData = (applicant: Applicant | undefined) => {
  return applicant?.status === Status.PendingExperienceData;
};

/**
 * Get URL of the profile of applicant on Twitter.
 * @param applicant Applicant object.
 * @returns Twitter URL.
 */
export const getTwitterUrl = (applicant: Applicant | undefined) => {
  return applicant?.twitter
    ? `https://twitter.com/${applicant?.twitter}`
    : undefined;
};

/**
 * Get URL of the profile of applicant on Instagram.
 * @param applicant Applicant object.
 * @returns Instagram URL.
 */
export const getInstagramUrl = (applicant: Applicant | undefined) => {
  return applicant?.instagram
    ? `https://instagram.com/${applicant?.instagram}`
    : undefined;
};

/**
 * Get URL of the profile of applicant on Tiktok.
 * @param applicant Applicant object.
 * @returns Tiktok URL.
 */
export const getTiktokUrl = (applicant: Applicant | undefined) => {
  return applicant?.tiktok
    ? `https://tiktok.com/@${applicant?.tiktok}`
    : undefined;
};

/**
 * Get URL of the number of applicant on WhatsApp.
 * @param applicant Applicant object.
 * @returns WhatsApp URL.
 */
export const getWhatsAppUrl = (applicant: Applicant | undefined) => {
  return applicant?.mobile ? `https://wa.me/${applicant?.mobile}` : undefined;
};

/**
 * Check if the applicant has the mobile number.
 * @param applicant Applicant data.
 */
export const hasMobile = (applicant?: Applicant | undefined) => {
  return Boolean(applicant?.mobile);
};

/**
 * Get a formatted disabilities string.
 * @param disabilities Disability array.
 * @returns Disabilities string.
 */
export const getFormattedDisabilities = (disabilities?: Disability[]) => {
  if (disabilities?.length > 0) {
    return disabilities.map((disability) => disability.name).join(", ");
  }
};

/**
 * Format extracurricular title.
 */
export const getFormattedExtracurriularTitle = (
  extracurricular: Extracurricular,
) => {
  return extracurricular?.organization
    ? `${extracurricular.name} - ${extracurricular?.organization}`
    : extracurricular.name;
};

/**
 * Get the last Job
 * @param data Applicant data.
 */
export const getLastJob = ({ experience }: Applicant) => {
  return experience[experience.length - 1];
};

/**
 * Get the last education
 * @param data Applicant data.
 */
export const getLastEducation = ({ education }: Applicant) => {
  return education
    .sort((a, b) => (a.level.sequence < b.level.sequence ? 1 : -1))
    .sort(compareStartDateInverted)[0];
};

/**
 * Validate applicant data. Throws on violations.
 * @param data Applicant data.
 */
export const validate = async ({ id, ...data }: Applicant) => {
  await api.request({
    url: id ? `/applicants/${id}` : "/applicants",
    method: id ? "PATCH" : "POST",
    data,
    params: { validate: 1 },
  });
};
